import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Copy, RefreshCw, Check } from "lucide-react";
import { textTools } from "../data/tools";
import { ToolShell, inputCls } from "../components/ToolShell";
import { AdSlot } from "../components/AdSlot";
import { usePageMeta } from "../lib/usePageMeta";
import { trackUsage } from "../lib/api";
import NotFound from "./NotFound";

function CopyBtn({ text, testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      data-testid={testid}
      onClick={() => { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}
      className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-2 border-ink bg-paper px-4 py-2.5 hover:bg-gold transition-colors"
    >
      {done ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
    </button>
  );
}

const Stat = ({ label, value }) => (
  <div className="border-2 border-ink bg-paper p-4 text-center">
    <div className="font-mono text-3xl font-bold text-signal">{value}</div>
    <div className="font-mono text-[10px] uppercase tracking-widest text-ink/50 mt-1">{label}</div>
  </div>
);

function WordCounter() {
  const [text, setText] = useState("");
  const s = useMemo(() => {
    const t = text.trim();
    const words = t ? t.split(/\s+/).length : 0;
    const sentences = t ? (t.match(/[.!?]+/g) || []).length || 1 : 0;
    return { words, chars: text.length, charsNoSpace: text.replace(/\s/g, "").length, sentences, read: Math.ceil(words / 200) };
  }, [text]);
  return (
    <div className="space-y-6">
      <textarea data-testid="wc-input" value={text} onChange={(e) => setText(e.target.value)} rows={8}
        placeholder="Start typing or paste your text…" className={`${inputCls} resize-y`} />
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="wc-stats">
        <Stat label="Words" value={s.words} />
        <Stat label="Characters" value={s.chars} />
        <Stat label="No spaces" value={s.charsNoSpace} />
        <Stat label="Sentences" value={s.sentences} />
        <Stat label="Min read" value={s.read} />
      </div>
    </div>
  );
}

function CaseConverter() {
  const [text, setText] = useState("");
  const toTitle = (t) => t.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  const toSentence = (t) => t.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, (c) => c.toUpperCase());
  const cases = [
    { id: "upper", label: "UPPERCASE", fn: (t) => t.toUpperCase() },
    { id: "lower", label: "lowercase", fn: (t) => t.toLowerCase() },
    { id: "title", label: "Title Case", fn: toTitle },
    { id: "sentence", label: "Sentence case", fn: toSentence },
  ];
  return (
    <div className="space-y-6">
      <textarea data-testid="cc-input" value={text} onChange={(e) => setText(e.target.value)} rows={5}
        placeholder="Type text to transform…" className={`${inputCls} resize-y`} />
      <div className="space-y-4">
        {cases.map((c) => (
          <div key={c.id} className="border-2 border-ink bg-paper">
            <div className="flex items-center justify-between border-b-2 border-ink px-4 py-2 bg-canvas">
              <span className="font-mono text-[11px] uppercase tracking-widest">{c.label}</span>
              <CopyBtn testid={`cc-copy-${c.id}`} text={c.fn(text)} />
            </div>
            <div data-testid={`cc-out-${c.id}`} className="px-4 py-3 font-mono text-sm break-words min-h-[44px]">{c.fn(text)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PasswordGen() {
  const [len, setLen] = useState(16);
  const [opts, setOpts] = useState({ upper: true, lower: true, num: true, sym: true });
  const [pw, setPw] = useState("");
  const gen = () => {
    const sets = { upper: "ABCDEFGHJKLMNPQRSTUVWXYZ", lower: "abcdefghijkmnopqrstuvwxyz", num: "23456789", sym: "!@#$%^&*-_=+?" };
    let pool = "";
    Object.keys(opts).forEach((k) => { if (opts[k]) pool += sets[k]; });
    if (!pool) { setPw(""); return; }
    let out = "";
    const arr = new Uint32Array(len);
    crypto.getRandomValues(arr);
    for (let i = 0; i < len; i++) out += pool[arr[i] % pool.length];
    setPw(out);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { gen(); }, [len, opts]);
  return (
    <div className="space-y-6">
      <div className="border-2 border-ink bg-ink text-paper p-6 flex items-center justify-between gap-4">
        <span data-testid="pw-output" className="font-mono text-lg sm:text-2xl break-all text-gold">{pw || "—"}</span>
        <div className="flex gap-2 shrink-0">
          <button data-testid="pw-regenerate" onClick={gen} className="w-11 h-11 border-2 border-paper flex items-center justify-center hover:bg-paper hover:text-ink transition-colors"><RefreshCw className="w-5 h-5" /></button>
          <CopyBtn testid="pw-copy" text={pw} />
        </div>
      </div>
      <div>
        <div className="flex justify-between font-mono text-[11px] uppercase tracking-widest text-ink/50 mb-2">
          <span>Length</span><span data-testid="pw-length-val">{len}</span>
        </div>
        <input data-testid="pw-length" type="range" min="6" max="48" value={len} onChange={(e) => setLen(+e.target.value)} className="w-full accent-signal" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["upper", "A-Z"], ["lower", "a-z"], ["num", "0-9"], ["sym", "!@#"]].map(([k, lbl]) => (
          <button key={k} data-testid={`pw-toggle-${k}`} onClick={() => setOpts({ ...opts, [k]: !opts[k] })}
            className={`font-mono text-sm border-2 border-ink py-3 transition-colors ${opts[k] ? "bg-signal text-paper" : "bg-paper"}`}>{lbl}</button>
        ))}
      </div>
    </div>
  );
}

function LoremGen() {
  const W = "lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua enim ad minim veniam quis nostrud exercitation ullamco laboris nisi aliquip ex ea commodo consequat".split(" ");
  const [n, setN] = useState(3);
  const [text, setText] = useState("");
  const gen = () => {
    const paras = [];
    for (let p = 0; p < n; p++) {
      const sents = [];
      const sc = 4 + Math.floor(Math.random() * 3);
      for (let i = 0; i < sc; i++) {
        const wl = 6 + Math.floor(Math.random() * 8);
        let words = [];
        for (let j = 0; j < wl; j++) words.push(W[Math.floor(Math.random() * W.length)]);
        let s = words.join(" ");
        sents.push(s.charAt(0).toUpperCase() + s.slice(1) + ".");
      }
      paras.push(sents.join(" "));
    }
    setText(paras.join("\n\n"));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { gen(); }, [n]);
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <span className="font-mono text-[11px] uppercase tracking-widest text-ink/50">Paragraphs</span>
        <input data-testid="lorem-count" type="number" min="1" max="20" value={n} onChange={(e) => setN(Math.max(1, +e.target.value))} className="w-24 font-mono border-2 border-ink px-3 py-2 outline-none focus:border-signal" />
        <CopyBtn testid="lorem-copy" text={text} />
      </div>
      <div data-testid="lorem-output" className="border-2 border-ink bg-paper p-6 whitespace-pre-line leading-relaxed text-ink/80">{text}</div>
    </div>
  );
}

const MAP = { "word-counter": WordCounter, "case-converter": CaseConverter, "password-generator": PasswordGen, "lorem-ipsum": LoremGen };

export default function TextToolPage() {
  const { slug } = useParams();
  const meta = textTools.find((t) => t.slug === slug);
  const Comp = MAP[slug];
  usePageMeta(meta ? `${meta.title} — Free Online | Utiliverse` : "Text Tool", meta?.blurb || "");
  useEffect(() => {
    if (meta) trackUsage({ slug: `text-${slug}`, title: meta.title, path: `/text-tools/${slug}`, category: "text" });
  }, [meta, slug]);
  if (!meta || !Comp) return <NotFound />;
  return (
    <ToolShell crumb="Text Tools" crumbTo="/text-tools" title={meta.title} subtitle={meta.blurb} testid={`text-tool-page-${slug}`}>
      <Comp />
      <div className="mt-12"><AdSlot format="leaderboard" testid="text-tool-ad" /></div>
    </ToolShell>
  );
}
